<template>
  <AuModal
    class="capital-modal"
    :model-value="showDialog"
    width="480px"
    :height="heightModal"
    :border-radius="15"
    padding="15px 20px 26px 20px"
    @update:modelValue="$emit('update:modelValue', false)"
  >
    <template #header>
      <span
        v-if="step < 5 && owner"
        class="gray-color"
      >{{ step }} from 4</span>
    </template>

    <template #body>
      <div
        v-if="!owner"
        class="main-error"
      >
        {{
          $t("auCapitals.capitalModal.error", { token: project.mnemonicEng })
        }}
      </div>

      <template v-else-if="step == 1">
        <div
          v-if="isStakingPool"
          class="title clamp-text"
        >
          {{ $t(`auCapitals.capitalModal.1step.titleStakingPool`) }}
        </div>
        <div
          v-else
          class="title clamp-text"
        >
          {{ $t(`auCapitals.capitalModal.1step.title${vote}`) }}
          {{ project.name }}
        </div>
        <div class="subtitle">
          {{ $t(`auCapitals.capitalModal.1step.subtitle${vote}`) }}
        </div>

        <div class="inputs">
          <div class="line">
            <AuSelect
              :value="token"
              disabled
              :options="tokensOptions"
              :placeholder="$t(`auCapitals.capitalModal.1step.payWith${vote}`)"
              :select-label="$t(`auCapitals.capitalModal.1step.payWith${vote}`)"
              @input="(value) => (token = value)"
            />

            <div class="info-block">
              <span>{{ $t("auCapitals.capitalModal.1step.yourBalance") }}</span>
              <span>
                <AuDecimalStatic
                  :value="owner.amountFree"
                  :decimal="owner.auToken.decimals"
                  :indent="owner.auToken.indent"
                  :show-addition="false"
                />
                <RouterLink
                  class="link"
                  :to="isNotRwa ? '/my-assets/buy-form' : '/my-assets/card-buy-form'"
                >
                  {{ $t("auCapitals.capitalModal.1step.deposit") }}
                </RouterLink>
              </span>
            </div>
          </div>
          <div v-if="project?.applicationType == 'stakingPool' && testerRole">
            <AuList :items="infoStep1" />
          </div>

          <div class="line">
            <div class="inputs">
              <div>
                <div class="label">
                  {{
                    $t(`auCapitals.capitalModal.1step.amount${vote}`) +
                      " " +
                      project.mnemonicEng
                  }}
                </div>

                <AuInput
                  id="for-test-money-input"
                  :model-value="moneyAmount"
                  :min="min"
                  :max="max"
                  type="Number"
                  @update:modelValue="setMoneyAmount"
                />
              </div>
            </div>

            <div class="info-block">
              <span>{{
                $t(`auCapitals.capitalModal.1step.amountOfTokens${postfix}`)
              }}</span>
              <span><AuDecimalStatic
                :value="tokenCount"
                :decimal="0"
                :indent="0"
                :show-addition="false"
              /></span>
            </div>
          </div>
        </div>

        <div class="mt-2 d-flex justify-content-between gray-color grey-info">
          <span class="d-flex flex-column">
            <span>
              {{ $t("auCapitals.capitalModal.1step.min") }}
              <AuDecimalStatic
                :value="min"
                :decimal="0"
                :addition="project.mnemonicEng"
              />
            </span>
            <span>
              {{ $t("auCapitals.capitalModal.1step.max") }}
              <AuDecimalStatic
                :value="max"
                :decimal="0"
                :addition="project.mnemonicEng"
              />
            </span>
          </span>
          <span class="text-end">{{
            $t("auCapitals.capitalModal.1step.instantly")
          }}</span>
        </div>

        <div class="text-balance">
          <span class="gray-color">{{
            $t("auCapitals.capitalModal.1step.fromBalance")
          }}</span>
          <span><AuDecimalStatic
            :value="owner.amountFree"
            :decimal="owner.auToken.decimals"
            :indent="owner.auToken.indent"
            :show-addition="false"
          />
          </span>
        </div>

        <div class="btn-block">
          <AuButton
            bordered
            width="82px"
            height="34px"
            center
            :border-radius="12"
            @click="getAmount(25)"
          >
            <span class="percent"> 25% </span>
          </AuButton>
          <AuButton
            bordered
            width="82px"
            height="34px"
            center
            :border-radius="12"
            @click="getAmount(50)"
          >
            <span class="percent"> 50% </span>
          </AuButton>
          <AuButton
            bordered
            width="82px"
            height="34px"
            center
            :border-radius="12"
            @click="getAmount(75)"
          >
            <span class="percent"> 75% </span>
          </AuButton>
          <AuButton
            bordered
            width="82px"
            height="34px"
            center
            :border-radius="12"
            @click="getAmount(100)"
          >
            <span class="percent"> 100% </span>
          </AuButton>
        </div>
      </template>

      <template v-else-if="step == 2">
        <div class="title">
          {{ $t(`auCapitals.capitalModal.2step.title${vote}`) }}
        </div>

        <div class="subtitle">
          {{ $t(`auCapitals.capitalModal.2step.subtitle${vote}`) }}
        </div>

        <div
          v-if="projectTerms == 'autentic'"
          class="terms"
          :style="cssVars"
        >
          <TermsVote v-if="isVoting" />
          <Terms v-else />
        </div>
        <div
          v-else-if="projectTerms == 'equal'"
          class="terms"
          :style="cssVars"
        >
          <TermsEqual />
        </div>
        <div
          v-else
          class="terms"
          :style="cssVars"
        />

        <div class="text-[15px]">
          {{ $t("auCapitals.capitalModal.2step.terms") }}
        </div>

        <div class="d-flex flex-col gap-1 mt-1 ml-3">
          <div class="d-flex gap-2">
            <div>
              <input
                id="for-test-terms-checkbox"
                v-model="termsAgree"
                type="checkbox"
                class="checkbox"
              >
            </div>

            <label
              class="checkbox-label"
              for="termsCheckbox"
            >
              {{ $t("auCapitals.capitalModal.2step.termsAgree") }}
            </label>
          </div>

          <template v-if="!isVoting">
            <div
              v-for="(config, index) in checkboxesData"
              :key="index"
              class="d-flex gap-2"
            >
              <div>
                <input
                  :id="'memorandumCheckbox' + index"
                  v-model="checkboxesData[index].checked"
                  type="checkbox"
                  class="checkbox"
                >
              </div>

              <label
                class="checkbox-label"
                :for="'memorandumCheckbox' + index"
              >
                {{ config.text }}

                <label
                  class="link"
                  :disabled="checkboxesData[index].loading"
                  @click.prevent="tryDownloadMemorandum(index)"
                >
                  {{ config.linkText }}
                </label>
              </label>
            </div>
          </template>
        </div>
      </template>

      <template v-else-if="step == 3">
        <div class="title">
          {{ $t("auCapitals.capitalModal.3step.title") }}
        </div>

        <div class="subtitle">
          {{ $t(`auCapitals.capitalModal.3step.subtitle${vote}`) }}
        </div>

        <div class="verify">
          <AuList :items="infoStep3" />
        </div>
      </template>

      <template v-else-if="step == 4">
        <div class="title">
          {{ $t("auCapitals.capitalModal.4step.title") }}
        </div>

        <div class="subtitle">
          {{ $t("auCapitals.capitalModal.4step.subtitle") }}
        </div>

        <div class="d-flex align-items-center gap-3">
          <ProgressBar
            :procent="procent"
            green
          />

          <span> {{ procent }}%</span>
        </div>

        <div class="progresses">
          <div
            :checked="transactionsChecks.terms"
            class="progress-line"
          >
            <img src="/img/project-card/check.svg">
            <p>
              {{ $t("auCapitals.capitalModal.4step.terms") }}<span>...</span><span>OK</span>
            </p>
          </div>

          <div
            :checked="transactionsChecks.balance"
            class="progress-line"
          >
            <img src="/img/project-card/check.svg">
            <p>
              {{ $t("auCapitals.capitalModal.4step.balance") }}<span>...</span><span>OK</span>
            </p>
          </div>

          <div
            :checked="transactionsChecks.smartcontract"
            class="progress-line"
          >
            <img src="/img/project-card/check.svg">
            <p>
              {{ $t("auCapitals.capitalModal.4step.smartcontract")
              }}<span>...</span><span>OK</span>
            </p>
          </div>

          <div
            :checked="transactionsChecks.received"
            class="progress-line uppercase"
          >
            <img src="/img/project-card/check.svg">
            <p>
              {{ isOpenSale ? project?.tokenBase?.mnemonicEng : selectedToken }}
              {{ $t("auCapitals.capitalModal.4step.received") }}<span>...</span><span>OK</span>
            </p>
          </div>

          <div
            :checked="transactionsChecks.sent"
            class="progress-line"
          >
            <img src="/img/project-card/check.svg">
            <p>
              {{ $t("auCapitals.capitalModal.4step.sent") }}<span>...</span><span>OK</span>
            </p>
          </div>

          <span
            v-if="error"
            class="text-error"
          >{{ error }}</span>
        </div>
      </template>

      <template v-else-if="step == 5">
        <div class="final-titles">
          <div class="title">
            {{ $t("auCapitals.capitalModal.5step.title") }}
          </div>

          <div
            v-if="isStakingPool"
            class="subtitle clamp-text lh-style"
          >
            {{ $t("auCapitals.capitalModal.5step.subtitleStakingPool") }}
          </div>

          <div
            v-else
            class="subtitle clamp-text lh-style"
          >
            {{ subtitle }}
            <div>{{ clampedName }}.</div>
          </div>
        </div>

        <div class="image d-flex justify-content-center">
          <img src="/img/project-card/trophy.svg">
        </div>

        <div class="result">
          <span v-if="!isStakingPool">
            <AuDecimalStatic
              class="lh-style"
              :value="tokenCount"
              :show-addition="false"
            />
            {{ clampedName }}
            {{ $t("auCapitals.capitalModal.5step.credited") }}
          </span>

          <a
            class="link"
            target="_blank"
            :href="link"
          >
            {{ $t("auCapitals.capitalModal.5step.check") }}
          </a>
        </div>
      </template>
    </template>

    <template #footer>
      <div
        v-if="!owner"
        class="final-buttons"
      >
        <AuButton
          :disabled="!hasAccessToDeposit"
          bordered
          center
          active-bg-color="#2ABA7C"
          @click="isNotRwa ? $router.push({ name: 'buy form' }) : $router.push({ name: 'card buy form' })"
        >
          {{ $t("auCapitals.capitalModal.topUp") + " " + project.mnemonicEng }}
        </AuButton>
      </div>

      <div
        v-else-if="error"
        class="final-buttons"
      >
        <AuButton
          class="button"
          bordered
          center
          active-bg-color="#ba2a2a"
          @click="$emit('update:modelValue', false)"
        >
          {{ $t("auCapitals.capitalModal.close") }}
        </AuButton>
      </div>

      <div
        v-else-if="step < 5"
        :step="step"
        class="main-buttons"
      >
        <AuButton
          v-if="step > 1 && step < 4"
          class="back-button"
          bordered
          center
          @click="step--"
        >
          {{ $t("auCapitals.capitalModal.back") }}
        </AuButton>

        <div
          v-if="userLimitErrorText.length > 0"
          class="warning"
        >
          {{ userLimitErrorText[0] }}

          <AuDecimalStatic
            :value="userLimit"
            :decimal="0"
            :indent="project.mnemonicDecimals"
            :addition="tokensOptions[0]?.display"
            @click="fixLimit"
          />{{ userLimitErrorText[1] }}.
        </div>

        <span
          v-if="isAmountIncorrect && step <= 3 && isDirty"
          class="warning"
        >
          {{ $t("auCapitals.capitalModal.warning") }}
          <AuDecimalStatic
            :value="amountToFix"
            :decimal="0"
            :indent="project.mnemonicDecimals"
            :addition="tokensOptions[0]?.display"
            @click="fixAmount"
          />
        </span>
        <span v-else />

        <AuButton
          id="for-test-button-next"
          class="button"
          bordered
          center
          type="primary"
          active-bg-color="#2ABA7C"
          :disabled="isNextBolcked"
          @click="step++"
        >
          {{ $t("auCapitals.capitalModal.next") }}
        </AuButton>
      </div>

      <div
        v-else
        class="final-buttons"
      >
        <AuButton
          class="button"
          bordered
          center
          type="primary"
          active-bg-color="#2ABA7C"
          @click="$emit('update:modelValue', false)"
        >
          {{ $t("auCapitals.capitalModal.anotherInvestment") }}
        </AuButton>
      </div>

      <span
        v-if="!hasAccessToDeposit"
        class="error mt-2"
      >
        {{ accessDeniedReasonsText }}
      </span>
    </template>
  </AuModal>
</template>

<script>
import _ from "lodash";
import BigNumber from "bignumber.js";
import moment from "moment";

import ProgressBar from "@/components/progressBar/ProgressBar";
import Terms from "./Terms";
import TermsEqual from "@/components/capital/TermsEqual";
import TermsVote from "@/components/capital/TermsVote";
import { checkAccess, checkPostfix, getAccessDeniedReasonsText, scope } from "@/help/accessData";

import { formatDecimalStatic } from "@/help/decimalStatic";
import { investmentFormCheckboxes } from "@/help/investmentFormCheckboxes";
import { mapActions, mapGetters } from "vuex";

export const STATES = {
  NOT_STARTED: 0,
  IN_PROGRESS: 1,
  DONE: 2,
  FAIL: 3
};

export default {
  components: {
    TermsVote,
    ProgressBar,
    Terms,
    TermsEqual
  },

  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    project: {
      type: Object,
      required: true
    },
    investAmount: {
      type: String,
      default: ""
    }
  },

  emits: ["update:modelValue", "finish"],

  data() {
    return {
      isDirty: false,
      moneyAmount: "",
      step: 1,
      token: "",
      termsAgree: false,
      error: "",
      STATES: STATES,
      address: "",
      hash: "",
      checkboxesData: [],
      transactionsChecks: {
        terms: STATES.NOT_STARTED,
        balance: STATES.NOT_STARTED,
        smartcontract: STATES.NOT_STARTED,
        received: STATES.NOT_STARTED,
        sent: STATES.NOT_STARTED
      }
    };
  },

  computed: {
    ...mapGetters({
      auOwners: "auOwners",
      tokens: "tokens",
      tokensFiat: "tokensFiat",
      tokensGold: "tokensGold",
      tokensUtility: "tokensUtility",
      defaultTokenId: "defaultToken/defaultTokenId",
      defaultTokenEnv: "defaultToken/defaultTokenEnv",
      isTestRole: "profile/isTestRole",
      profile: "profile/profile",
      investments: "capitals/investments"
    }),

    postfix() {
      return checkPostfix();
    },

    isNotRwa() {
      return checkAccess(scope.notRwa);
    },

    cssVars() {
      return {
        "--height": "200px"
      };
    },

    heightModal() {
      let height = 480;

      if (this.step == 1) {
        if (this.testerRole && this.project?.applicationType == "stakingPool") {
          height = 550;
        }
      }
      else if (this.step == 2) {
        height += this.checkboxesData.length * 21;
      }

      return height + "px";
    },

    testerRole() {
      return this.isTestRole;
    },

    amountAvailable() {
      if (this.isOpenSale) {
        return BigNumber(this.project?.openSaleLimit ?? 0).multipliedBy(BigNumber(10).pow(this.project.mnemonicDecimals)).toString(10);
      }
      else {
        return BigNumber(this.project.maxCap).minus(this.project?.currentCap).toString(10);
      }
    },

    infoStep1() {
      const addition = this.project.mnemonicEng;
      return [
        [
          { title: this.$t("auCapitals.projectCard.information.staked"), value: "XXX", addition },
          { title: this.$t("auCapitals.projectCard.information.available"), value: this.amountAvailable, decimal: this.project?.tokenQuote?.decimals, indent: this.project?.tokenQuote?.indent, addition: this.project?.tokenQuote?.mnemonicEng }
        ]
      ];
    },

    infoStep3() {
      return [
        [
          { title: this.$t("auCapitals.capitalModal.3step.project"), value: this.project.name },
          { title: this.$t(`auCapitals.capitalModal.3step.payWith${this.vote}`), value: this.selectedToken },
          { title: this.$t(`auCapitals.capitalModal.3step.amount${this.vote}`), value: this.moneyAmount.toString().replace(",", ".") },
          { title: this.$t("auCapitals.capitalModal.3step.fees", { platform: process.env.VUE_APP_PLATFORM }), value: "-" }

        ]
      ];
    },

    infoStep4() {
      return [
        [
          { title: this.$t("auCapitals.capitalModal.3step.project"), value: this.project.name },
          { title: this.$t(`auCapitals.capitalModal.3step.payWith${this.vote}`), value: this.selectedToken },
          { title: this.$t(`auCapitals.capitalModal.3step.amount${this.vote}`), value: this.moneyAmount.toString().replace(",", ".") },
          { title: this.$t("auCapitals.capitalModal.3step.fees", { platform: process.env.VUE_APP_PLATFORM }), value: "-" }

        ]
      ];
    },

    currentToken() {
      return this.tokens.find(item => item.id == this.project.tokenQuoteId);
    },

    subtitle() {
      return this.isVoting ? this.$t("auCapitals.capitalModal.5step.subtitleVoted") : this.$t("auCapitals.capitalModal.5step.subtitle");
    },

    link() {
      let link = `${process.env.VUE_APP_SCAN_BASE_URL}`;
      if (this.hash) {
        link += "tx/" + this.hash;
      }
      return link;
    },

    priceBase() {
      let value = BigNumber(this.project.priceBase);
      value = value.dividedBy(BigNumber(10).pow(this.project.mnemonicDecimals));
      return value.toNumber();
    },

    min() {
      return this.minQuote ? _.max([this.priceBase, this.minQuote]) : this.priceBase;
    },

    max() {
      const maxAvailable = this.isOpenSale ? BigNumber(this.project?.openSaleLimit ?? 0).multipliedBy(BigNumber(10).pow(this.project.mnemonicDecimals)) :
        this.project.maxCap - this.project.currentCap;
      let userHasAmount = BigNumber(this.owner.amountFree);
      userHasAmount = userHasAmount.dividedBy(BigNumber(10).pow(this.project.mnemonicDecimals));
      userHasAmount = userHasAmount.dividedBy(this.priceBase).integerValue(BigNumber.ROUND_FLOOR);
      userHasAmount = userHasAmount.multipliedBy(this.priceBase).toNumber();
      let amountToMax = BigNumber(maxAvailable);
      amountToMax = amountToMax.dividedBy(BigNumber(10).pow(this.project.mnemonicDecimals)).toNumber();
      return this.maxQuote ? _.min([amountToMax, this.maxQuote, userHasAmount]) : _.min([amountToMax, userHasAmount]);
    },

    procent() {
      let procent = 0;

      for (const property in this.transactionsChecks) {
        if (this.transactionsChecks[property] == STATES.DONE) {
          procent += 20;
        }
      }
      return procent;
    },

    maxQuote() {
      let value = BigNumber(this.project.priceBase);
      value = value.dividedBy(BigNumber(10).pow(this.project.mnemonicDecimals));
      value = value.multipliedBy(this.project.quote);
      return value.toNumber();
    },

    minQuote() {
      let value = BigNumber(this.project.priceBase);
      value = value.dividedBy(BigNumber(10).pow(this.project.mnemonicDecimals));
      value = value.multipliedBy(this.project.minCount);
      return value.toNumber();
    },

    isEnough() {
      let count = BigNumber(this.moneyAmount.toString().replace(",", "."));

      if (this.owner === undefined) {
        return false;
      }
      let value = BigNumber(this.owner.amountFree);
      value = value.dividedBy(BigNumber(10).pow(this.project.mnemonicDecimals));
      return count.isLessThanOrEqualTo(value);
    },

    owner() {
      return this.auOwners.find(item => item.auToken.id == this.project.tokenQuoteId);
    },

    tokenCount() {
      let tokenCount = BigNumber(this.moneyAmount.toString().replace(",", "."));
      tokenCount = tokenCount.dividedBy(this.priceBase);
      return tokenCount.toNumber();
    },

    tokensOptions() {
      const res = _.find([...this.tokensUtility, ...this.tokensFiat, ...this.tokensGold], token => token.mnemonicEng === this.project.mnemonicEng);
      return [{
        display: res?.mnemonicEng,
        value: res?.id
      }];
    },

    isNextBolcked() {
      if (this.isAmountIncorrect) {
        return true;
      }

      if (!this.isEnough) {
        return true;
      }

      if (this.moneyAmount < this.min) {
        return true;
      }

      if (this.step == 1 && this.moneyAmount == 0) {
        return true;
      }

      if (this.step == 2 && !(this.termsAgree && _.every(this.checkboxesData, agreement => agreement.checked))) {
        return true;
      }

      if (this.step == 4 && this.transactionsChecks.received != STATES.DONE) {
        return true;
      }

      if (this.userLimitErrorText.length > 0) {
        return true;
      }

      return false;
    },

    clampedName() {
      // if (this.project.name?.length >= 15) {
      //   return this.project.name.slice(0, 15) + "...";
      // }

      return this.project.name;
    },

    selectedToken() {
      return _.chain(this.tokensOptions).find({ value: this.token }).get("display");
    },

    isAmountIncorrect() {
      return Math.trunc(this.tokenCount) !== Number(this.tokenCount);
    },

    amountToFix() {
      let amount = BigNumber(this.tokenCount).integerValue(BigNumber.ROUND_DOWN);
      amount = amount.multipliedBy(this.priceBase);
      return amount.isGreaterThan(this.min) ? amount.toString(10) : this.min;
    },

    isVoting() {
      return this.project?.projectType === "voting";
    },

    isStakingPool() {
      return this.project?.applicationType === "stakingPool";
    },

    projectTerms() {
      return process.env.VUE_APP_TERMS;
    },

    vote() {
      return this.isVoting ? "Vote" : "";
    },

    autMnemonic() {
      return process.env.VUE_APP_AUT_MNEMONIC;
    },

    hasAccessToDeposit() {
      return checkAccess(scope.deposit);
    },

    accessDeniedReasonsText() {
      const textParams = { platform: process.env.VUE_APP_PLATFORM };
      return getAccessDeniedReasonsText(scope.deposit, textParams);
    },

    isOpenSale() {
      return this.project?.hasOpenSale && this.project.isSuccess;
    },

    defaultToken() {
      return _.find(this.tokens, x => x.id == this.defaultTokenId);
    },

    yearTransferSum() {
      let sum = BigNumber(0);

      for (const investment of this.investments) {
        sum = BigNumber(investment?.sum).plus(sum);
      }

      return Number(formatDecimalStatic({
        value: sum,
        decimal: this.defaultToken?.decimals,
        indent: 2
      }).replace(/\s+/g, ""));
    },

    projectTransferSum() {
      const investment = _.find(this.investments, x => x.id == this.project.id);

      return Number(formatDecimalStatic({
        value: investment?.sum,
        decimal: this.defaultToken?.decimals,
        indent: 2
      }).replace(/\s+/g, ""));
    },

    userLimitErrorText() {
      if (this.isNotRwa) {
        return [];
      }

      const maxCap = formatDecimalStatic({
        value: this.project.maxCap,
        decimal: this.currentToken.decimals,
        indent: this.currentToken.indent
      }).replace(/\s+/g, "");

      if (Number(this.moneyAmount) > maxCap * 0.33) {
        return ["You can invest up to 33% of the project hard cap. Invest"];
      }

      const usdToken = _.find(this.auOwners, x => x.auToken.mnemonicEng.toLowerCase() == "usd");
      let projectLimit = 0;

      if (this.project.tokenQuote.mnemonicEng.toLowerCase() == "usd") {
        projectLimit = Number(process.env.VUE_APP_RETAIL_USER_PROJECT_LIMIT);
      }
      else {
        projectLimit = Number(process.env.VUE_APP_RETAIL_USER_PROJECT_LIMIT) * Number(usdToken?.rateDefaultToken);
      }

      if (Number(this.moneyAmount) > projectLimit - this.projectTransferSum) {
        return ["You can invest up to ", " more in this asset"];
      }

      let yearLimit = 0;

      if (this.project.tokenQuote.mnemonicEng.toLowerCase() == "usd") {
        yearLimit = Number(process.env.VUE_APP_RETAIL_USER_YEAR_LIMIT);
      }
      else {
        yearLimit = Number(process.env.VUE_APP_RETAIL_USER_YEAR_LIMIT) * Number(usdToken?.rateDefaultToken);
      }

      if (Number(this.moneyAmount) > yearLimit - this.yearTransferSum) {
        return ["You can invest up to ", " more in this year"];
      }

      return [];
    },

    userLimit() {
      if (this.isNotRwa) {
        return null;
      }

      const maxCap = formatDecimalStatic({
        value: this.project.maxCap,
        decimal: this.currentToken.decimals,
        indent: this.currentToken.indent
      }).replace(/\s+/g, "");

      if (Number(this.moneyAmount) > maxCap * 0.33) {
        return maxCap * 0.33;
      }

      const usdToken = _.find(this.auOwners, x => x.auToken.mnemonicEng.toLowerCase() == "usd");
      let projectLimit = 0;

      if (this.project.tokenQuote.mnemonicEng.toLowerCase() == "usd") {
        projectLimit = Number(process.env.VUE_APP_RETAIL_USER_PROJECT_LIMIT);
      }
      else {
        projectLimit = Number(process.env.VUE_APP_RETAIL_USER_PROJECT_LIMIT) * Number(usdToken?.rateDefaultToken);
      }

      if (Number(this.moneyAmount) > projectLimit - this.projectTransferSum) {
        return projectLimit - this.projectTransferSum;
      }

      let yearLimit = 0;

      if (this.project.tokenQuote.mnemonicEng.toLowerCase() == "usd") {
        yearLimit = Number(process.env.VUE_APP_RETAIL_USER_YEAR_LIMIT);
      }
      else {
        yearLimit = Number(process.env.VUE_APP_RETAIL_USER_YEAR_LIMIT) * Number(usdToken?.rateDefaultToken);
      }

      if (Number(this.moneyAmount) > yearLimit - this.yearTransferSum) {
        return yearLimit - this.yearTransferSum;
      }

      return null;
    }
  },

  watch: {
    async step(value) {
      if (value == 4) {
        this.transactionsChecks.terms = STATES.IN_PROGRESS;
        await new Promise(r => setTimeout(r, 500));
        this.transactionsChecks.terms = STATES.DONE;

        this.transactionsChecks.balance = STATES.IN_PROGRESS;
        try {
          await this.getAuOwners();

          if (!this.isEnough) {
            this.error = this.$t("auCapitals.capitalModal.errors.balance");
            this.transactionsChecks.balance = STATES.FAIL;
            return;
          }
        }
        catch (error) {
          this.error = this.$t("auCapitals.capitalModal.errors.balanceError");
          this.transactionsChecks.balance = STATES.FAIL;
          return;
        }
        this.transactionsChecks.balance = STATES.DONE;

        this.transactionsChecks.smartcontract = STATES.IN_PROGRESS;
        await new Promise(r => setTimeout(r, 500));
        this.transactionsChecks.smartcontract = STATES.DONE;

        this.transactionsChecks.received = STATES.IN_PROGRESS;
        let invest = null;
        try {
          if (this.isOpenSale) {
            invest = await this.getOpenSaleTokens();
          }
          else {
            invest = await this.investCapital();
          }
          if (!invest.data) {
            this.error = this.$t("auCapitals.capitalModal.errors.transaction");
            this.transactionsChecks.received = STATES.FAIL;
            return;
          }
          else if (invest.status == 267) {
            const message = invest.data.message || invest.data.detail;
            if (message) {
              this.error = message;
            }
            else {
              this.error = this.$t("auCapitals.capitalModal.errors.purchase");
            }
            this.transactionsChecks.received = STATES.FAIL;
            return;
          }
        }
        catch (error) {
          this.error = this.$t("auCapitals.capitalModal.errors.transaction");
          this.transactionsChecks.received = STATES.FAIL;
          return;
        }
        this.transactionsChecks.received = STATES.DONE;

        this.transactionsChecks.sent = STATES.IN_PROGRESS;
        if (!this.isOpenSale) {
          for (;;) {
            try {
              const data = await this.getTokenEventById(invest.data?.id);
              if (data.isTaskDone) {
                this.address = data.address;
                const transactions = await this.getTransactionsByAddress({
                  addressFrom: this.owner.auToken?.createdAddress,
                  addressTo: data.address
                });
                this.hash = _.first(transactions)?.hash;

                if (data.value == "0") {
                  this.error = this.$t("auCapitals.capitalModal.errors.zeroValue");
                  this.transactionsChecks.sent = STATES.FAIL;
                  return;
                }

                this.moneyAmount = BigNumber(data.value).multipliedBy(this.priceBase).toNumber();
                break;
              }
              else if (data.isError) {
                this.error = this.$t("auCapitals.capitalModal.errors.transaction");
                this.transactionsChecks.sent = STATES.FAIL;
                return;
              }
            }
            catch (error) {
              this.error = this.$t("auCapitals.capitalModal.errors.checking");
              this.transactionsChecks.sent = STATES.FAIL;
              return;
            }

            await this.getInvestmentsNoPagination();
            await new Promise(r => setTimeout(r, 5000));
          }
        }
        this.$emit("finish");
        try {
          if (window.carrotquest && process.env.VUE_APP_CARROT_ENABLED === "true") {
            window.carrotquest.track("invest", {
              "address": this.address,
              "amount": Math.trunc(this.tokenCount).toString(10)
            });
          }
        }
        catch (error) {
          console.error(error);
        }

        this.transactionsChecks.sent = STATES.DONE;
      }
    },
    investAmount: {
      handler(newVal) {
        if (newVal) {
          this.moneyAmount = newVal;
        }
      },
      immediate: true
    }
  },

  async created() {
    await this.getBalances(this.defaultTokenId);
    await this.getAuOwners();

    this.checkboxesData = investmentFormCheckboxes(process.env.VUE_APP_VENDOR, this.project.id, this.project.nameEng);
  },

  async mounted() {
    this.token = this.project.tokenQuoteId;
  },

  methods: {
    ...mapActions({
      sendTokenEvents: "sendTokenEvents",
      getTokenEventById: "getTokenEventById",
      getAuOwners: "getAuOwners",
      getBalances: "getBalances",
      getTransactionsByAddress: "getTransactionsByAddress",
      downloadMemorandum: "capitals/downloadMemorandum",
      getInvestmentsNoPagination: "capitals/getInvestmentsNoPagination",
      investInOpenSale: "investInOpenSale"
    }),

    setMoneyAmount(value) {
      this.isDirty = true;
      this.moneyAmount = value;
    },

    async tryDownloadMemorandum(index) {
      const checkboxData = this.checkboxesData[index];

      if (!checkboxData.loading) {
        try {
          checkboxData.loading = true;
          await this.downloadMemorandum({
            url: checkboxData.link,
            name: checkboxData.name,
            sum: /memorandum/i.test(checkboxData.name) ? this.moneyAmount : null
          });
        }
        catch (error) {
          console.error(error);
        }
        finally {
          checkboxData.loading = false;
        }
      }
    },

    getAmount(percent) {
      let amountFree = BigNumber(this.owner.amountFree);
      amountFree = amountFree.dividedBy(BigNumber(10).pow(this.project.mnemonicDecimals));
      let calculatedAmount = amountFree.multipliedBy(percent).dividedBy(100);

      calculatedAmount = calculatedAmount.integerValue(BigNumber.ROUND_UP);

      if (calculatedAmount.isGreaterThanOrEqualTo(this.max)) {
        this.moneyAmount = this.max.toString();
      }
      else {
        this.moneyAmount = calculatedAmount.toString(10);
      }
      this.fixAmount();
    },

    fixAmount() {
      this.moneyAmount = BigNumber(Math.trunc(this.tokenCount)).multipliedBy(this.priceBase).toString();
    },

    fixLimit() {
      this.moneyAmount = BigNumber(this.userLimit).multipliedBy(this.priceBase).toString();
    },

    async investCapital() {
      const params = {
        user: `/api/users/${this.$auth.user().id}`,
        createdAt: moment(),
        type: "token_capital_buy",
        description: "buy",
        value: Math.trunc(this.tokenCount).toString(10),
        address: "",
        isTask: true,
        auCapital: `api/au_capitals/${this.project.id}`
      };

      const invest = await this.sendTokenEvents(params);
      return invest;
    },

    async getOpenSaleTokens() {
      const params = {
        value: Math.trunc(this.tokenCount).toString(10)
      };
      return await this.investInOpenSale({ id: this.project.id, params: params });
    }
  }
};
</script>

<style lang="scss" scoped>
.capital-modal {
  button.btn.button {
    div.btn__text {
      color: white;
    }
  }

  button.btn.back-button {
    border-color: var(--btn-dg-color-primary) !important;

    div.btn__text {
      color: var(--btn-dg-color-primary);
    }
  }

  .text-error {
    color: red;
  }

  .main-error {
    height: 300px;
    display: flex;
    align-items: center;
    font-size: 18px;
    text-align: center;
  }

  .image {
    height: 116px;
  }

  .final-buttons {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .main-buttons {
    display: flex;
    justify-content: space-between;

    &[step="4"] {
      justify-content: end;
    }
  }

  .progresses {
    display: flex;
    flex-direction: column;

    gap: 8px;

    margin-top: 10px;

    .progress-line {
      display: flex;
      position: relative;

      &[checked="1"] {
        span {
          &:first-child {
            display: block;

            clip-path: inset(0 10px 0 0);
            animation: l 1s steps(4) infinite;

            @keyframes l {
              to {
                clip-path: inset(0 -3px 0 0);
              }
            }
          }
        }
      }

      &[checked="3"] {
        color: red;
      }

      &[checked="2"] {
        img {
          display: block;
        }

        span {
          display: block;
        }
      }

      span {
        display: none;
      }

      img {
        display: none;
        position: absolute;
        top: 6px;
      }

      p {
        margin-left: 21px;
        margin-bottom: 0;

        display: flex;
        gap: 5px;
      }
    }
  }

  .final-titles {
    margin-top: 25px;

    text-align: center;
  }

  .warning {
    color: red;
    margin-right: 25px;

    &:not(:first-child) {
      margin-left: 10px;
      margin-right: 10px;
    }

    > span {
      cursor: pointer;
      text-decoration: underline;
    }
  }

  .indent_progress {
    width: 100%;
  }

  .gray-color {
    color: var(--color-50);
  }
  .grey-info {
    font-size: 12px;
  }
  .percent {
    font-size: 14px;
  }

  .title {
    font-size: 24px;
    line-height: 24px;
    margin-top: 12px;
    margin-bottom: 10px;
  }

  .clamp-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .subtitle {
    font-size: 15px;
    line-height: 15px;
    margin-bottom: 24px;
    white-space: nowrap;
  }

  .lh-style {
    line-height: 1.2em;
  }

  .result {
    display: flex;
    flex-direction: column;
    text-align: center;

    gap: 10px;

    > span {
      margin: 0 60px;
    }
  }

  .terms {
    margin-top: -13px;
    margin-bottom: 15px;
    padding: 5px 12px;

    height: var(--height);

    overflow-y: scroll;
    overflow-x: hidden;

    font-size: 12px;
    line-height: 24px;

    background: var(--sep-line-color);
  }

  .checkbox {
    display: flex;
    justify-content: center;
    align-items: center;

    width: 14px;
    height: 14px;
    vertical-align: middle;
    border: 1px solid var(--text-color);
    appearance: none;
    -webkit-appearance: none;
    outline: none;
    cursor: pointer;
    position: relative;

    margin-top: 4px;

    &:checked {
      &::before {
        content: url("~/public/svg/invest/check.svg");
        margin-top: 1px;
        filter: var(--text-filter);
      }
    }

    &-label {
      cursor: pointer;
    }
  }

  .verify {
    display: flex;
    flex-direction: column;

    gap: 12px;

    .verify-line {
      display: flex;
      justify-content: space-between;

      padding-bottom: 10px;
      border-bottom: 1px var(--color-50) dashed;

      .label {
        font-size: 13px;
        color: var(--color-50);
      }

      .value {
        text-align: end;
      }

      &:last-child {
        border: none;
      }
    }
  }

  .inputs {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .label {
      font-size: 13px;
      color: var(--color-40);
      margin-bottom: 6px;
    }

    .line {
      display: flex;
      justify-content: space-between;
      align-items: end;

      .input-label {
        margin-bottom: 4px;
      }

      .au-select-label {
        margin-bottom: 4px;
      }

      .input {
        display: flex;
        justify-content: space-between;
        align-items: end;
        gap: 10px;
      }

      > div {
        &:first-child {
          width: 60%;
        }

        &:last-child {
          text-align: end;
        }
      }

      .info-block {
        display: flex;
        flex-direction: column;
        font-size: 13px;
        gap: 6px;
        line-height: 14px;
        padding-bottom: 6px;
      }
    }
  }

  .link {
    color: #279063;
    text-decoration: underline;
    cursor: pointer;

    &:not([disabled="true"]) {
      &:active {
        color: #279063a3;
        &:hover {
          color: #279063a3;
        }
      }

      &:hover {
        color: #279063d1;
      }
    }

    &[disabled="true"] {
      color: #124630;
      cursor: auto;
    }
  }
}

.text-balance {
  margin-top: 14px;
}

.btn-block {
  margin-top: 6px;
  margin-bottom: 10px;
  display: flex;
  gap: 4px;
}
</style>